/**
 * EventBridge - UI Layer Service
 *
 * Handles all DOM event dispatching for engine → UI communication.
 * This service bridges the gap between the pure game engine (no DOM/React)
 * and the React UI layer.
 *
 * IMPORTANT: This file belongs in the UI layer and should NEVER be imported
 * by engine files. The engine communicates through EventManager only.
 */

import { EventManager, ZodiacEngineEvent } from '../../services/zodiacEngine/Managers/EventManager';
import { EventType, SelectPieceEvent } from '../../services/zodiacEngine/const';
import { Position } from '../../services/zodiacEngine/types';

export class EventBridge {
  private static eventManager: EventManager | null = null;
  private static unsubscribe: (() => void) | null = null;

  /**
   * Initialize the EventBridge to listen to engine events
   * and dispatch them as DOM CustomEvents
   */
  static initialize(eventManager: EventManager): void {
    if (this.eventManager) {
      this.cleanup();
    }

    this.eventManager = eventManager;

    // Subscribe to all engine events and dispatch as DOM events
    this.unsubscribe = eventManager.subscribe((event: ZodiacEngineEvent) => {
      this.handleEngineEvent(event);
    });

  }

  /**
   * Cleanup subscriptions and reset state
   */
  static cleanup(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    this.eventManager = null;
  }

  /**
   * Handle engine events and dispatch to DOM
   */
  private static handleEngineEvent(event: ZodiacEngineEvent): void {
    // For now, we primarily care about selectPiece events for bonus turn auto-selection
    if (event.type === EventType.selectPiece) {
      this.dispatchPieceEvent(event as any); // Type assertion needed due to ZodiacEvent union
    }

    // Dispatch all events as generic zodiac-event for backward compatibility
    this.dispatchToDOM(event);
  }

  /**
   * Dispatch a piece selection event with lock
   * (Used for Virus/Monkey/Penguin bonus turn auto-selection)
   */
  static dispatchPieceEvent(event: SelectPieceEvent): void {
    const customEvent = new CustomEvent('zodiac-event', {
      detail: event,
    });

    document.dispatchEvent(customEvent);

    if (event.lockSelection) {
    }
  }

  /**
   * Generic DOM event dispatch for any engine event
   */
  private static dispatchToDOM(event: ZodiacEngineEvent): void {
    const customEvent = new CustomEvent('zodiac-event', {
      detail: event,
    });

    document.dispatchEvent(customEvent);
  }

  /**
   * Dispatch a selection lock event (legacy compatibility)
   */
  static dispatchSelectionLock(pieceId: string, position: Position): void {
    const selectEvent: SelectPieceEvent = {
      type: EventType.selectPiece,
      pieceId: pieceId,
      position: position,
      lockSelection: true,
    };

    this.dispatchPieceEvent(selectEvent);
  }
}
